import { Type } from '@angular/core';

export interface LazyElementEntry {
    tagName: string;
    loadComponent: () => Promise<Type<unknown>>;
}

/**
 * Components published as `cs-v20-*` custom elements but not imported until their tag actually
 * appears in the document — see `registerLazyElements` for how the upgrade happens. Kept out of
 * `elementList` on purpose: that file's static imports are what pin a component into the eager
 * production bundle, defeating the point of lazy-loading it.
 *
 * Grouping below follows real import edges found in each component's own file (its `imports:`
 * array, not comments) — components in the same group depend on each other directly, so moving
 * one without the other would silently pin the "moved" one's code back into whichever group
 * member is still (or becomes) eager. Before moving anything OUT of this list back to
 * `elementList`, grep the rest of this file and `elementList` for its class name to check it
 * isn't someone else's dependency, the same way cs-ag-grid was blocked by
 * defects-and-recommendations-card.
 */
export const lazyElementList: LazyElementEntry[] = [
    {
        tagName: 'cs-v20-ag-grid',
        loadComponent: () =>
            import('@app/shared/cs-ag-grid/cs-ag-grid.component').then((m) => m.CsAgGridComponent),
    },
    {
        tagName: 'cs-v20-dynamic-certificate-form-parser',
        loadComponent: () =>
            import('@app/shared/dynamic-certificate-form-parser/dynamic-certificate-form-parser.component').then(
                (m) => m.DynamicCertificateFormParserComponent,
            ),
    },
    {
        // Moved here alongside cs-ag-grid: its own template eagerly embeds <cs-ag-grid>, so as
        // long as it stayed in elementList it pinned ag-grid's real weight into the eager bundle.
        tagName: 'cs-v20-defects-and-recommendations-card',
        loadComponent: () =>
            import('@app/shared/defects-and-recommendations-card/defects-and-recommendations-card').then(
                (m) => m.DefectsAndRecommendationsCard,
            ),
    },

    // -- search-input group: cs-filter/cs-color-picker/map-layer-toggle/view-task-details all embed cs-search-input directly --
    {
        tagName: 'cs-v20-search-input',
        loadComponent: () =>
            import('@app/shared/cs-search-input/cs-search-input.component').then((m) => m.CsSearchInput),
    },
    {
        tagName: 'cs-v20-filter',
        loadComponent: () => import('@app/shared/filters/cs-filter/cs-filter.component').then((m) => m.CsFilterComponent),
    },
    {
        tagName: 'cs-v20-inline-item',
        loadComponent: () =>
            import('@app/shared/filters/cs-filter/cs-inline-item/cs-inline-item.component').then(
                (m) => m.CsInlineItemComponent,
            ),
    },
    {
        tagName: 'cs-v20-color-picker',
        loadComponent: () => import('@app/shared/cs-color-picker/cs-color-picker').then((m) => m.CsColorPicker),
    },
    {
        tagName: 'cs-v20-map-layer-toggle',
        loadComponent: () => import('@app/shared/map-layer-toggle/map-layer-toggle').then((m) => m.MapLayerToggleComponent),
    },
    {
        tagName: 'cs-v20-view-task-details',
        loadComponent: () =>
            import('@app/shared/view-task-details/view-task-details.component').then((m) => m.ViewTaskDetailsComponent),
    },

    // -- search-template group: cs-search-template embeds template-modal, which embeds quill-editor; form-search-panel embeds cs-search-template --
    {
        tagName: 'cs-v20-search-template',
        loadComponent: () =>
            import('@app/shared/cs-search-template-component/cs-search-template.component').then(
                (m) => m.CsSearchTemplateComponent,
            ),
    },
    {
        tagName: 'cs-v20-template-modal',
        loadComponent: () =>
            import('@app/shared/cs-search-template-component/modal/template-modal/template-modal.component').then(
                (m) => m.TemplateModalComponent,
            ),
    },
    {
        tagName: 'cs-v20-quill-editor',
        loadComponent: () => import('@app/shared/cs-quill-editor/cs-quill-editor.component').then((m) => m.CsQuillEditorComponent),
    },
    {
        tagName: 'cs-v20-form-search-panel',
        loadComponent: () => import('@app/shared/form-search-panel/form-search-panel').then((m) => m.FormSearchPanel),
    },

    // -- fields group: fields-side-panel-modal embeds fields-item --
    {
        tagName: 'cs-v20-fields-item',
        loadComponent: () => import('@app/shared/fields-components/fields-item/fields-item').then((m) => m.FieldsItem),
    },
    {
        tagName: 'cs-v20-fields-side-panel-modal',
        loadComponent: () =>
            import('@app/shared/fields-components/fields-side-panel-modal/fields-side-panel-modal').then(
                (m) => m.FieldsSidePanelModal,
            ),
    },

    // -- payment group: payment-handler embeds stored-card-handler --
    {
        tagName: 'cs-v20-payment-handler',
        loadComponent: () => import('@app/shared/payment-handler/payment-handler').then((m) => m.PaymentHandler),
    },
    {
        tagName: 'cs-v20-stored-card-handler',
        loadComponent: () => import('@app/shared/stored-card-handler/stored-card-handler').then((m) => m.StoredCardHandler),
    },

    // -- task/kanban group: kanban-board embeds task-input-box (via its own card/column subtree), task-input-box embeds dynamic-dropdown --
    {
        tagName: 'cs-v20-task-input-box',
        loadComponent: () => import('@app/shared/task-input-box/task-input-box.component').then((m) => m.TaskInputBoxComponent),
    },
    {
        tagName: 'cs-v20-dynamic-dropdown',
        loadComponent: () =>
            import('@app/shared/dynamic-dropdown/dynamic-dropdown.component').then((m) => m.DynamicDropdownComponent),
    },
    {
        tagName: 'cs-v20-kanban-board',
        loadComponent: () => import('@app/shared/kanban-board/kanban-board').then((m) => m.KanbanBoard),
    },

    // -- segmentations group: segmentations-layout embeds banner --
    {
        tagName: 'cs-v20-segmentations-layout',
        loadComponent: () =>
            import('@app/shared/segmentations-mapping/layouts/segmentations-layout').then((m) => m.SegmentationsLayout),
    },
    {
        tagName: 'cs-v20-banner',
        loadComponent: () => import('@app/shared/banner/banner').then((m) => m.Banner),
    },

    // -- fields/renderer components only consumed (today) by the already-lazy form parser above --
    {
        tagName: 'cs-v20-image-file-uploader',
        loadComponent: () =>
            import('@app/shared/image-file-uploader/image-file-uploader.component').then(
                (m) => m.ImageFileUploaderComponent,
            ),
    },
    {
        tagName: 'cs-v20-signature-field',
        loadComponent: () => import('@app/shared/signature-field/signature-field.component').then((m) => m.SignatureFieldComponent),
    },

    // -- independent: no other elementList/lazyElementList entry imports these directly --
    {
        tagName: 'cs-v20-contact-field',
        loadComponent: () => import('@app/shared/custom-contact-field/custom-contact-field').then((m) => m.CustomContactField),
    },
    {
        tagName: 'cs-v20-no-data',
        loadComponent: () => import('@app/shared/no-data/no-data').then((m) => m.NoData),
    },
    {
        tagName: 'cs-v20-datepicker',
        loadComponent: () => import('@app/shared/cs-datepicker/cs-datepicker.component').then((m) => m.CsDatepickerComponent),
    },
    {
        tagName: 'cs-v20-pagination',
        loadComponent: () => import('@app/shared/cs-pagination/cs-pagination.component').then((m) => m.CsPaginationComponent),
    },
    {
        tagName: 'cs-v20-new-time-picker',
        loadComponent: () => import('@app/shared/new-time-picker/new-time-picker.component').then((m) => m.NewTimePickerComponent),
    },
    {
        tagName: 'cs-v20-slider',
        loadComponent: () => import('@app/shared/cs-slider/cs-slider.component').then((m) => m.CsSliderComponent),
    },
    {
        tagName: 'cs-v20-confirmation-message',
        loadComponent: () =>
            import('@app/shared/confirmation-message/confirmation-message').then((m) => m.ConfirmationMessage),
    },
    {
        tagName: 'cs-v20-card-field-setup',
        loadComponent: () =>
            import('@app/shared/cs-card-field-setup/cs-card-field-setup.component').then((m) => m.CsCardFieldSetupComponent),
    },
    {
        tagName: 'cs-v20-view-filter-setup',
        loadComponent: () =>
            import('@app/shared/cs-view-filter-setup/cs-view-filter-setup.component').then((m) => m.CsViewFilterSetupComponent),
    },
    {
        tagName: 'cs-v20-icon-picker',
        loadComponent: () => import('@app/shared/cs-icon-picker/cs-icon-picker.component').then((m) => m.CsIconPickerComponent),
    },
    {
        tagName: 'cs-v20-share-with',
        loadComponent: () => import('@app/shared/cs-share-with/cs-share-with.component').then((m) => m.CsShareWithComponent),
    },
    {
        tagName: 'cs-v20-stepper-wizard-layout',
        loadComponent: () =>
            import('@app/shared/stepper-wizard-layout/stepper-wizard-layout.component').then(
                (m) => m.StepperWizardLayoutComponent,
            ),
    },
    {
        tagName: 'cs-v20-perfect-scrollbar',
        loadComponent: () =>
            import('@app/shared/perfect-scrollbar/perfect-scrollbar.component').then((m) => m.PerfectScrollbarComponent),
    },
    {
        tagName: 'cs-v20-ai-textarea',
        loadComponent: () => import('@app/shared/ai-textarea/ai-textarea').then((m) => m.AiTextarea),
    },
    {
        tagName: 'cs-v20-google-address-autocomplete',
        loadComponent: () =>
            import('@app/shared/google-address-autocomplete/google-address-autocomplete.component').then(
                (m) => m.GoogleAddressAutocompleteComponent,
            ),
    },
    {
        tagName: 'cs-v20-workflow-feedback-viewer',
        loadComponent: () =>
            import('@app/shared/workflow-feedback-viewer/workflow-feedback-viewer.component').then(
                (m) => m.WorkflowFeedbackViewerComponent,
            ),
    },
    {
        tagName: 'cs-v20-photo-gallery',
        loadComponent: () => import('@app/shared/photo-gallery/photo-gallery.component').then((m) => m.PhotoGalleryComponent),
    },
    {
        tagName: 'cs-v20-form-marketplace',
        loadComponent: () =>
            import('@app/shared/cs-form-marketplace/cs-form-marketplace.component').then((m) => m.CsFormMarketplaceComponent),
    },
    {
        tagName: 'cs-v20-job-report-summary',
        loadComponent: () =>
            import('@app/shared/job-report-summary/job-report-summary.component').then((m) => m.JobReportSummaryComponent),
    },
    {
        // JOBS-1441/dev merge: dev removed lookup-table entirely (LUNA-24), replacing it with
        // simple-settings-table below - the cs-v20-lookup-table entry here is dropped to match.
        tagName: 'cs-v20-simple-settings-table',
        loadComponent: () =>
            import('@app/shared/simple-settings-table/simple-settings-table.component').then(
                (m) => m.SimpleSettingsTableComponent,
            ),
    },
    {
        tagName: 'cs-v20-save-button',
        loadComponent: () => import('@app/shared/save-button/save-button.component').then((m) => m.SaveButtonComponent),
    },
    {
        tagName: 'cs-v20-pdf-builder',
        loadComponent: () => import('@app/shared/pdf-builder/pdf-builder.component').then((m) => m.PdfBuilderComponent),
    },
    {
        tagName: 'cs-v20-split-button',
        loadComponent: () =>
            import('@app/shared/cs-split-button/cs-split-button.component').then((m) => m.CsSplitButtonComponent),
    },
];
